import { captureException } from '@sentry/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useEffect } from 'react';

export const scrollToBottom = (virtualizer, { observationTime = 450, imageLoadTimeout = 2000 } = {}) => {
  const lastIndex = virtualizer?.options?.count - 1;

  // When images load in, the layout will change and the virtualizer will need to be scrolled to the bottom again
  const observer = new window.MutationObserver((records) => {
    for (const record of records) {
      for (let node of record.addedNodes) {
        if (typeof node?.querySelectorAll !== 'function') {
          continue;
        }

        const images = node.querySelectorAll('img');

        // Scrolls to bottom once all images have loaded in
        if (images.length > 0) {
          let loadedCount = 0;
          let timeoutId;

          const realignAndRemoveLoadListeners = () => {
            forceScrollToBottom();
            // Clean up listeners from all images
            images.forEach((image) => {
              image.removeEventListener('load', handleImageLoad);
            });
            // Clean up timeout
            if (timeoutId) {
              clearTimeout(timeoutId);
            }
          };

          const handleImageLoad = () => {
            loadedCount++;
            // Scroll to bottom once all images have loaded in
            if (loadedCount === images.length) {
              realignAndRemoveLoadListeners();
            }
          };

          // Add load listeners to all images
          images.forEach((image) => {
            image.addEventListener('load', handleImageLoad);
          });

          // Cleanup after timeout (as a fallback)
          timeoutId = setTimeout(() => {
            forceScrollToBottom();
            images.forEach((image) => {
              image.removeEventListener('load', handleImageLoad);
            });
          }, imageLoadTimeout);
        }
      }
    }
  });

  if (virtualizer?.options?.count <= 0) return observer;

  const forceScrollToBottom = () => {
    /** Scroll to the bottom of the virtualizer */
    const scroll = () => {
      const totalSize = virtualizer.getTotalSize();
      virtualizer.scrollToOffset(totalSize);
    };

    if (window.requestAnimationFrame) {
      // Use multiple animation frames to ensure measurements are fully complete
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          scroll();
        });
      });
    } else {
      // For browsers that don't support requestAnimationFrame, fallback to setTimeout
      setTimeout(() => {
        scroll();
      }, 100);
    }
  };

  try {
    forceScrollToBottom();
  } catch (error) {
    captureException(error, {
      extra: {
        lastIndex
      }
    });
  }

  const messageItems = virtualizer.scrollElement.querySelector('.messages-items');

  if (!messageItems) return observer;

  observer.observe(messageItems, {
    childList: true,
    subtree: true
  });

  setTimeout(() => {
    observer.disconnect();
  }, observationTime);

  return observer;
};

export const useVirtualizedMessages = ({ messages = [], getScrollElement, open, estimateSize }) => {
  const virtualizer = useVirtualizer({
    count: messages.length,
    getScrollElement,
    estimateSize,
    onChange: () => {},
    overscan: 5,
    scrollPaddingStart: 8,
    scrollPaddingEnd: 8,
    paddingStart: 8,
    paddingEnd: 8
  });

  useEffect(() => {
    if (!open || !virtualizer.scrollElement) return;

    const observer = scrollToBottom(virtualizer);

    return () => {
      observer.disconnect();
    };
  }, [open, virtualizer, messages]);

  return [messages, virtualizer];
};
